import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import { MissionCardBody } from '../../shared/CardComponents';
import CardContextMenu from './CardContextMenu';
import DragHandleMenu from '../../FlowchartView/TaskNode/DragHandleMenu';
import { CATEGORY_COLORS } from '../../FlowchartView/TaskNode/constants';
import { getPrimaryTag } from '../../hooks/useTagActions';
import { useIconPicker } from '../../hooks/useIconPicker';
import IconPicker from '../../shared/IconPicker';

/**
 * KanbanCard Component
 * 
 * Kanban view wrapper for MissionCardBody.
 * 
 * This component handles view-specific concerns:
 * - Click overlay for selection
 * - Drag handle with menu (via CardContextMenu)
 * - Selection state styling
 * 
 * The visual card content is rendered by MissionCardBody (shared with Flow).
 */
const KanbanCard = ({ 
  task, 
  category,
  allCategories = [],
  allTags = [],
  allTasks = [],
  statuses,
  nestedSprint,
  nestedSprints = [],
  allSprints = [],
  isSelected,
  isEditingTitle,
  onFinishTitleEdit,
  onSelect,
  onCategoryChange,
  onRenameCategory,
  onDeleteCategory,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onStatusChange,
  onOpenDetails,
  onOpenAttachments,
  onOpenNestedSprint,
  onCreateNestedSprint,
  onRemoveNestedSprint,
  onIconChange,
  onRename,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onPriorityChange,
  onDifficultyChange,
  onCustomFieldValueChange,
  customFields = [],
  onDeleteTask,
  onCopyCard,
  onDuplicateCard,
  onCreateParallelMission,
  onInsertMissionBefore,
  onSetStatusAutoSync,
  onDeliverablesChange,
  onInputsChange,
  onArchiveTask,
  onUnarchiveTask,
  onDependencyChange,
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  isBlocked,
  lockState,
  cardDisplaySettings = {},
  isTemplate = false,
  // Status management (for status dropdown)
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  // From SortableCard wrapper
  dragListeners,
  isDraggingCard,
  // View navigation (for "Open in view" button)
  currentView,
  customViews = [],
  activeCustomViewId = null,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Deliverables warning support
  onSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  onEditDeliverables,
  onClearDeliverable,
  onNavigateToParentMission,
  onGoToDependency,
}) => {
  const cardRef = useRef(null);
  const contextMenuRef = useRef(null);
  const dragHandleMenuRef = useRef(null);

  // Icon picker hook
  const iconPicker = useIconPicker({
    taskId: task.id,
    onIconChange,
  });

  // Scroll card into view when editing starts (for newly created cards)
  useEffect(() => {
    if (isEditingTitle && cardRef.current) {
      const timer = setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isEditingTitle]);

  // Listen for external requests to open drag handle menu (from CardContextMenu drag handle)
  useEffect(() => {
    const handleOpenMenu = (e) => {
      if (e.detail.taskId === task.id && dragHandleMenuRef.current) {
        if (e.detail.position) {
          // Direct position (from right-click)
          dragHandleMenuRef.current.openAtPosition(e.detail.position.x, e.detail.position.y);
        } else if (e.detail.handleRect) {
          // Position relative to handle (from left-click on drag handle)
          const { handleRect } = e.detail;
          const viewportHeight = window.innerHeight;
          const menuHeight = 350;
          
          let top = handleRect.bottom + 4;
          if (top + menuHeight > viewportHeight) {
            top = viewportHeight - menuHeight - 10;
          }
          
          // Position menu to the left of the handle
          let left = handleRect.right - 180;
          if (left < 10) {
            left = 10;
          }
          
          dragHandleMenuRef.current.openAtPosition(left, top);
        }
      }
    };
    
    const handleCloseMenu = (e) => {
      if (e.detail.taskId === task.id && dragHandleMenuRef.current) {
        dragHandleMenuRef.current.closeMenu();
      }
    };
    
    window.addEventListener('openKanbanCardMenu', handleOpenMenu);
    window.addEventListener('closeKanbanCardMenu', handleCloseMenu);
    
    return () => {
      window.removeEventListener('openKanbanCardMenu', handleOpenMenu);
      window.removeEventListener('closeKanbanCardMenu', handleCloseMenu);
    };
  }, [task.id]);

  // Context menu action handlers
  const handleDelete = useCallback(() => {
    onDeleteTask?.(task.id);
  }, [onDeleteTask, task.id]);

  const handleDuplicate = useCallback(() => {
    onDuplicateCard?.(task.id);
  }, [onDuplicateCard, task.id]);

  const handleCreateParallelMission = useCallback(() => {
    onCreateParallelMission?.(task.id);
  }, [onCreateParallelMission, task.id]);

  const handleInsertMissionBefore = useCallback(() => {
    onInsertMissionBefore?.(task.id);
  }, [onInsertMissionBefore, task.id]);

  const handleCopy = useCallback(() => {
    onCopyCard?.(task.id);
  }, [onCopyCard, task.id]);

  const handleOpenDetailsFromMenu = useCallback(() => {
    onOpenDetails?.(task.id);
  }, [onOpenDetails, task.id]);

  const handleOpenNestedSprintFromMenu = useCallback(() => {
    onOpenNestedSprint?.(task.nestedSprintId);
  }, [onOpenNestedSprint, task.nestedSprintId]);

  const handleCreateNestedSprintFromMenu = useCallback(() => {
    onCreateNestedSprint?.(task.id);
  }, [onCreateNestedSprint, task.id]);

  const handleAddIconFromMenu = useCallback((position) => {
    iconPicker.openPicker(null, position);
  }, [iconPicker]);

  const handleOpenAttachmentsFromMenu = useCallback(() => {
    onOpenAttachments?.(task.id);
  }, [onOpenAttachments, task.id]);

  const handleArchiveFromMenu = useCallback(() => {
    onArchiveTask?.(task.id);
  }, [onArchiveTask, task.id]);

  const handleUnarchiveFromMenu = useCallback(() => {
    onUnarchiveTask?.(task.id);
  }, [onUnarchiveTask, task.id]);

  // Handle right-click to open context menu (simple version - no drag tracking needed in Kanban)
  const handleContextMenu = useCallback((e) => {
    // Always prevent the default browser context menu
    e.preventDefault();
    e.stopPropagation();
    
    // Check if the click target is an interactive element
    // NOTE: We intentionally exclude [role="button"] because dnd-kit adds this to the sortable wrapper
    const target = e.target;
    const isInteractive = target.closest('button, input, select, textarea, .status-dropdown, .priority-dropdown, .difficulty-dropdown, .custom-field-dropdown, .task-node__tags-btn, .kanban-card__drag-handle');
    
    if (!isInteractive && dragHandleMenuRef.current) {
      dragHandleMenuRef.current.openAtPosition(e.clientX, e.clientY);
    }
  }, []);

  // Get primary tag for styling
  const availableTags = allTags.length > 0 ? allTags : allCategories;
  const primaryTagName = getPrimaryTag(task);
  const currentCategory = category || (primaryTagName 
    ? availableTags.find(t => t.name === primaryTagName) || { name: primaryTagName, color: CATEGORY_COLORS[0] }
    : null);

  const isLocked = lockState?.isLocked && task.status !== 'completed';
  const isExcluded = lockState?.isExcluded;
  
  // NOTE: 'kanban-card--selected' is NOT included here - it's managed via DOM manipulation
  // in useKanbanSelection for instant visual feedback. React state still tracks selection
  // for other purposes (bulk actions, clipboard, etc.) but the visual class is DOM-driven.
  const cardClasses = [
    'kanban-card',
    isDraggingCard && 'kanban-card--dragging',
    task.nodeType === 'decision' && 'kanban-card--decision',
    isBlocked && 'kanban-card--blocked',
    isExcluded && 'kanban-card--excluded',
    isLocked && 'kanban-card--locked',
    task.archived && 'kanban-card--archived',
  ].filter(Boolean).join(' ');

  // Click overlay handler - primary selection mechanism
  const handleOverlayClick = (e) => {
    e.stopPropagation();
    onSelect?.(task.id, e.shiftKey);
  };

  return (
    <div
      ref={cardRef}
      className={cardClasses}
      style={{
        '--category-color': currentCategory?.color || 'transparent',
        '--category-bg': currentCategory ? `${currentCategory.color}18` : 'transparent',
      }}
      data-task-id={task.id}
      onContextMenu={handleContextMenu}
    >
      {/* Click overlay - captures clicks for selection and right-click menu */}
      <div 
        className="kanban-card__click-overlay"
        onClick={handleOverlayClick}
        onContextMenu={handleContextMenu}
      />
      
      {/* Drag handle (dispatches event to open menu) */}
      <CardContextMenu
        ref={contextMenuRef}
        task={task}
        dragListeners={dragListeners}
      />

      {/* Shared menu component from Flow view - ONE menu for all views */}
      <DragHandleMenu
        ref={dragHandleMenuRef}
        task={task}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onCreateParallelMission={handleCreateParallelMission}
        onInsertMissionBefore={handleInsertMissionBefore}
        onCopy={handleCopy}
        onFocusTask={null} // Kanban doesn't have focus task
        hasNestedSprint={!!task.nestedSprintId}
        onOpenNestedSprint={handleOpenNestedSprintFromMenu}
        onCreateNestedSprint={handleCreateNestedSprintFromMenu}
        hasIcon={!!task.icon}
        onAddIcon={iconPicker.openPicker}
        onOpenDetails={handleOpenDetailsFromMenu}
        onOpenAttachments={handleOpenAttachmentsFromMenu}
        onArchive={handleArchiveFromMenu}
        onUnarchive={handleUnarchiveFromMenu}
        currentView={currentView}
        customViews={customViews}
        activeCustomViewId={activeCustomViewId}
        onViewChange={onViewChange}
        onSelectCustomView={onSelectCustomView}
        onGoToTaskInView={onGoToTaskInView}
      />

      {/* Icon Picker */}
      {iconPicker.showPicker && (
        <IconPicker
          currentIcon={task.icon}
          onIconChange={iconPicker.changeIcon}
          onClose={iconPicker.closePicker}
          position={iconPicker.pickerPosition}
          nodeColor={currentCategory?.color}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SHARED CARD BODY - All visual content from here
          ═══════════════════════════════════════════════════════════════ */}
      <MissionCardBody
        task={task}
        category={currentCategory}
        allTags={availableTags}
        allTasks={allTasks}
        statuses={statuses}
        nestedSprint={nestedSprint}
        nestedSprints={nestedSprints}
        allSprints={allSprints}
        isBlocked={isBlocked}
        isLocked={isLocked}
        isExcluded={isExcluded}
        lockReason={lockState?.lockReason}
        blockingDependencies={lockState?.blockingTasks || []}
        parentMissionName={lockState?.parentMissionName}
        parentCampaignName={lockState?.parentCampaignName}
        parentMissionId={lockState?.parentMissionId}
        parentSprintId={lockState?.parentSprintId}
        onNavigateToParentMission={onNavigateToParentMission}
        cardDisplaySettings={cardDisplaySettings}
        isTemplate={isTemplate}
        onGoToDependency={onGoToDependency || ((depId) => {
          const targetCard = document.querySelector(`[data-task-id="${depId}"]`);
          if (targetCard) {
            targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
            targetCard.classList.add('kanban-card--highlighted');
            setTimeout(() => {
              targetCard.classList.remove('kanban-card--highlighted');
            }, 2000);
          }
        })}
        onSubmitDeliverable={onSubmitDeliverable}
        onEditDeliverables={onEditDeliverables}
        onClearDeliverable={onClearDeliverable}
        showCategoryBar={false}
        isEditingTitle={isEditingTitle}
        onFinishTitleEdit={onFinishTitleEdit}
        onRename={onRename}
        onIconChange={onIconChange}
        onStatusChange={onStatusChange}
        onAddTag={onAddTag || onCategoryChange}
        onRemoveTag={onRemoveTag}
        onSetPrimaryTag={onSetPrimaryTag}
        onRenameTag={onRenameTag || onRenameCategory}
        onDeleteTag={onDeleteTag || onDeleteCategory}
        onOpenDetails={onOpenDetails}
        onOpenAttachments={onOpenAttachments}
        onOpenNestedSprint={onOpenNestedSprint}
        onRemoveNestedSprint={onRemoveNestedSprint}
        onDueDateChange={onDueDateChange}
        onRemindersChange={onRemindersChange}
        onEstimatedDurationMinutesChange={onEstimatedDurationMinutesChange}
        onCreatedAtChange={onCreatedAtChange}
        onStartedAtChange={onStartedAtChange}
        onCompletedAtChange={onCompletedAtChange}
        onUpdatedAtChange={onUpdatedAtChange}
        onPriorityChange={onPriorityChange}
        onDifficultyChange={onDifficultyChange}
        onCustomFieldValueChange={onCustomFieldValueChange}
        customFields={customFields}
        onSetStatusAutoSync={onSetStatusAutoSync}
        onArchiveTask={onArchiveTask}
        onUnarchiveTask={onUnarchiveTask}
        onDependencyChange={onDependencyChange}
        onAnswerChange={onDecisionAnswerChange}
        onOptionsChange={onDecisionOptionsChange}
        onAddStatus={onAddStatus}
        onDeleteStatus={onDeleteStatus}
        onRenameStatus={onRenameStatus}
        currentView={currentView}
        customViews={customViews}
        activeCustomViewId={activeCustomViewId}
        onViewChange={onViewChange}
        onSelectCustomView={onSelectCustomView}
        onGoToTaskInView={onGoToTaskInView}
      />

    </div>
  );
};

/**
 * Custom memo comparator for KanbanCard
 * Only re-render when visually meaningful props change.
 * Handlers are stable (from useCallback), so we skip comparing them.
 */
function areKanbanCardPropsEqual(prevProps, nextProps) {
  // Fast path: always re-render if selection state changed
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  
  // Re-render if task data changed
  if (prevProps.task !== nextProps.task) return false;
  
  // Re-render if editing state changed
  if (prevProps.isEditingTitle !== nextProps.isEditingTitle) return false;
  
  // Re-render if drag state changed
  if (prevProps.isDraggingCard !== nextProps.isDraggingCard) return false;
  
  // Re-render if blocked state changed
  if (prevProps.isBlocked !== nextProps.isBlocked) return false;
  
  // Re-render if lock state changed
  if (prevProps.lockState !== nextProps.lockState) return false;
  
  // Re-render if category changed
  if (prevProps.category !== nextProps.category) return false;
  
  // Re-render if nested sprint changed
  if (prevProps.nestedSprint !== nextProps.nestedSprint) return false;
  if (prevProps.nestedSprints !== nextProps.nestedSprints) return false;
  if (prevProps.allSprints !== nextProps.allSprints) return false;
  
  // Re-render if display settings changed
  if (prevProps.cardDisplaySettings !== nextProps.cardDisplaySettings) return false;
  
  // Re-render if statuses changed (affects status dropdown)
  if (prevProps.statuses !== nextProps.statuses) return false;
  
  // Re-render if tags changed
  if (prevProps.allTags !== nextProps.allTags) return false;
  
  // Skip comparing handlers - they're stable from useCallback
  // Skip comparing allTasks, allCategories - they're only used for dropdowns
  
  return true;
}

export default memo(KanbanCard, areKanbanCardPropsEqual);
